import React, { useState } from "react";
import { Card, Badge, Collapse } from "react-bootstrap";
import { Tracker } from "../pages/Dashboard";
import TrackerCard from "./TrackerCard";
import { useI18n } from "../i18n";

interface MonthCardProps {
  monthYear: string; // e.g., "May 2026"
  trackers: Tracker[];
  notificationCount: number;
  onDelete: (id: number) => void;
  onDeleteMonth?: (year: number, month: number) => void;
  onShowChart: (tracker: Tracker) => void;
  onEditFlight: (trackerId: number, flightType: "outbound" | "return") => void;
  onThresholdUpdated: () => void;
}

const MonthCard: React.FC<MonthCardProps> = ({
  monthYear,
  trackers,
  notificationCount,
  onDelete,
  onDeleteMonth,
  onShowChart,
  onEditFlight,
  onThresholdUpdated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useI18n();

  const handleCardClick = (e: React.MouseEvent) => {
    // Only toggle if clicking the card itself, not buttons inside trackers
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.month-card-header')) {
      setExpanded(!expanded);
    }
  };

  return (
    <Card 
      className={`month-card mb-3 ${expanded ? 'expanded' : ''} ${notificationCount > 0 ? 'has-notifications' : ''}`}
      style={{ cursor: 'pointer' }}
    >
      <Card.Header 
        className="month-card-header d-flex justify-content-between align-items-center"
        onClick={handleCardClick}
      >
        <div className="d-flex align-items-center">
          <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
          <h5 className="mb-0">
            <i className="bi bi-calendar-month me-2"></i>
            {monthYear}
          </h5>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Badge bg="secondary" pill>
            {trackers.length} {trackers.length === 1 ? t("monthCard.tracker") : t("monthCard.trackers")}
          </Badge>
          {notificationCount > 0 && (
            <Badge bg="danger" pill className="notification-badge pulse">
              <i className="bi bi-bell-fill me-1"></i>
              {notificationCount}
            </Badge>
          )}
          {onDeleteMonth && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                // parse month and year from monthYear string (e.g., "May 2026")
                const parts = monthYear.split(' ');
                const year = parseInt(parts[1], 10);
                const monthName = parts[0];
                const monthNamesEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                const monthNamesIt = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
                let month = monthNamesEn.indexOf(monthName) + 1;
                if (month === 0) {
                  month = monthNamesIt.indexOf(monthName) + 1;
                }
                if (!isNaN(year) && month >= 1 && month <= 12) {
                  // Request deletion via parent (parent will show a modal confirm)
                  onDeleteMonth(year, month);
                }
              }}
              title={t('delete') || 'Delete'}
            >
              <i className="bi bi-trash"></i>
            </button>
          )}
        </div>
      </Card.Header>
      
      <Collapse in={expanded}>
        <div>
          <Card.Body>
            <div className="row">
              {trackers.map((tracker) => (
                <div key={tracker.id} className="col-md-6 col-lg-4 mb-3">
                  <TrackerCard
                    tracker={tracker}
                    onDelete={onDelete}
                    onShowChart={onShowChart}
                    onEditFlight={onEditFlight}
                    onThresholdUpdated={onThresholdUpdated}
                  />
                </div>
              ))}
            </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default MonthCard;
